import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { Subscription }                        from 'rxjs';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

export type Line = { path: string, strokePath: string, label: string, color: string, fill: string };
export type Point = { x: number, y: number, value: number | string, color: string };

@Component({
  standalone: false,
  selector:    '[cs-line-chart]',
  templateUrl: './line-chart.component.html'
})
export class CsLineChartComponent implements OnInit, OnChanges {
  @Input('config') config: ChartConfig;

  maxValue: number;
  minValue        = 0;   // lower bound of the value axis (negative when data dips below zero)
  pointTooltips: string[] = [];
  yStep           = 5;
  xStep: number;
  yFactor: number = 0;
  xOffsets        = 50; // for the Y-axis labels
  yOffsets        = 25; // for the X-axis labels
  // animattion intro
  lines: Line[];
  points: Point[][];
  texts             = [];
  axisConfig;
  _hoveredIndex;
  hoveredPointIndex = -1;
  mousePosition     = null;

  // animation intro.
  private animationSubscribtion: Subscription;
  private animated;
  private currentAnimationStep  = 1;
  private animationStep         = 0.05;
  private animatioFrameInterval = 40;
  private hoveredText: any      = {};

  get hoveredIndex() {
    return this._hoveredIndex;
  }

  set hoveredIndex(i) {
    this._hoveredIndex = i;
    this.hoveredText   = i === -1 ? {} : this.getTooltip(i);
  }

  constructor() {
  }

  private isDataReady() {
    return this.config && this.config.data && this.config.data.datasets && this.config.data.datasets.length;
  }

  ngOnInit() {
    setTimeout(() => {
      if (this.isDataReady()) {
        this.initMaxValue();
        this.calculate();
      }
    }, 1);

  }

  ngOnChanges(changes: any) {
    if (changes.hasOwnProperty('config') && this.isDataReady()) {
      this.initMaxValue();
      this.calculate();
    }
  }

  onMouseMoveMainChart  = (e: MouseEvent) => {
    // // To be moved on top svg element!
    // this.mousePosition = {
    //     x: e.offsetX,
    //     y: e.offsetY
    // };
  };
  onMouseLeaveMainChart = (e: MouseEvent) => {
    this.mousePosition = null;
  };

  initMaxValue() {
    let rawMax: number;
    let rawMin = 0;
    if (this.config.isStacked) {
      const dataSegments = this.config.data.datasets
                               .map((d) => d.data);
      let maxSum         = 0;
      for (let j = 0; j < dataSegments[0].length; j++) {
        let sum = 0;
        for (let i = 0; i < dataSegments.length; i++) {
          sum += dataSegments[i][j];
        }
        maxSum = Math.max(maxSum, sum);
      }
      rawMax = maxSum;
    } else {
      const values  = this.config.data.datasets
                          .map((d) => d.data)
                          .reduce((a, b) => a.concat(b), []);
      rawMax = Math.max.apply(this, values);
      rawMin = Math.min.apply(this, values);
    }
    rawMax = Math.max(rawMax, 0);
    rawMin = Math.min(rawMin, 0);

    if (rawMin < 0) {
      // Negatives present: pick one round step and snap min & max to multiples of it, so the
      // axis spans below zero with round ticks instead of clipping points beneath the baseline.
      const magnitude = Math.max(Math.abs(rawMax), Math.abs(rawMin), 1);
      this.yStep      = ChartHelper.getMaxStepValue(magnitude, this.config.ySteps) / this.config.ySteps;
      this.maxValue   = Math.ceil(rawMax / this.yStep) * this.yStep;
      this.minValue   = Math.floor(rawMin / this.yStep) * this.yStep;
    } else {
      // All non-negative: identical to the original positive-only behaviour.
      this.maxValue = ChartHelper.getMaxStepValue(rawMax, this.config.ySteps);
      this.minValue = 0;
      this.yStep    = this.maxValue / this.config.ySteps;
    }
    if (this.maxValue === this.minValue) {
      this.maxValue = this.minValue + (this.yStep || 1);
    }
  }

  initAxisConfig() {
    this.axisConfig        = {chartWidth: this.config.chartWidth};
    this.axisConfig.s      = {
      x: Math.round(this.config.xPadding + this.xOffsets) + 0.5, y: Math.round(this.config.yPadding - this.yOffsets) + 0.5
    };
    this.axisConfig.e      = {
      // Right inset mirrors the left one (xOffsets), so the plot sits symmetrically and the
      // axis/gridlines end where the last data point lands.
      x: Math.round(this.config.chartWidth - this.config.xPadding - this.xOffsets) + 0.5,
      y: Math.round(this.config.chartHeight - this.config.yPadding - this.yOffsets) + 0.5
    };
    this.axisConfig.ySteps = [];
    this.axisConfig.xSteps = [];
    const stepCount = Math.max(1, Math.round((this.maxValue - this.minValue) / this.yStep));
    // Positive-only charts start at i=1 (unchanged); negative-aware charts also draw the 0 tick.
    for (let i = (this.minValue < 0 ? 0 : 1); i <= stepCount; i++) {
      const value = this.minValue + i * this.yStep;
      const step  = {
        text:   ChartHelper.getValueFormatted(Math.round(value)),
        // The zero gridline is the visual reference when lines dip below it — drawn stronger.
        isZero: this.minValue < 0 && value === 0,
        y:      Math.round(this.config.chartHeight - this.config.yPadding - this.yOffsets - (value - this.minValue) * this.yFactor) + 0.5
      };
      this.axisConfig.ySteps.push(step);
    }
    const labels = this.config.data.labels;
    for (let i = 0, l = labels.length; i < l; i++) {
      const step = {
        text: labels[i],
        x:    Math.round(this.config.xPadding + this.xOffsets + i * this.xStep) + 0.5
      };
      this.axisConfig.xSteps.push(step);
    }
  }

  initSteps() {
    const range  = (this.maxValue - this.minValue) || 1;
    // yStep is set in initMaxValue. xOffsets*2 (was *1.5) reserves right margin too, so the
    // last x-axis label is not clipped at the chart edge.
    // tslint:disable-next-line:max-line-length
    this.xStep   = (this.config.chartWidth - this.config.xPadding * 2 - this.xOffsets * 2) / (this.config.data.datasets[0].data.length - 1);
    this.yFactor = (this.config.chartHeight - this.config.yPadding * 2 - this.yOffsets) / range;
    this.initAxisConfig();
  }

  calculate() {
    if (!this.config.chartWidth || !this.config.chartHeight) {
      return;
    }
    this.initSteps();
    this.lines  = [];
    this.points = [];
    const xPad  = this.config.xPadding + this.xOffsets;
    const yPad  = this.config.yPadding + this.yOffsets;
    // draw lines
    for (let index = 0, l = this.config.data.datasets.length; index < l; index++) {
      const path                = [];
      const linePoints: Point[] = [];
      const i                   = this.config.isStacked ? l - 1 - index : index;
      const values              = this.config.data.datasets
                                      .map((d) => d.data);
      const points              = this.config.isStacked
        ? values[0].map((_, j) => values.slice(0, i + 1).map((a) => a[j]).reduce((a, b) => a + b))
        : this.config.data.datasets[i].data;
      // Only the path gets the 'M' start — linePoints must NOT include it: the j-loop below
      // pushes index 0 again, and a duplicated first entry shifts every point index by one
      // (tooltips off-by-one; the last point binds an undefined tooltip and never opens).
      path.push('M', xPad, this.config.chartHeight - yPad - (points[0] - this.minValue) * this.yFactor);
      for (let j = 0; j < points.length * this.currentAnimationStep; j++) {
        path.push('L', xPad + j * this.xStep, this.config.chartHeight - yPad - (points[j] - this.minValue) * this.yFactor);
        // tslint:disable-next-line:max-line-length
        linePoints.push({
          x:     xPad + j * this.xStep,
          y:     this.config.chartHeight - yPad - (points[j] - this.minValue) * this.yFactor,
          value: ChartHelper.getValueLocale(points[j]),
          color: this.config.colors[i]
        });
      }
      // The open polyline is what the stroke draws; the closed copy (area only) is fill-only,
      // so the closing edges (down/along the baseline/up) never get outlined.
      const strokePath = path.join(' ');
      if (this.config.type === 'area' && this.currentAnimationStep === 1) {
        // FILL
        path.push('L', xPad + (points.length - 1) * this.xStep, this.config.chartHeight - yPad + this.minValue * this.yFactor);
        path.push('L', xPad, this.config.chartHeight - yPad + this.minValue * this.yFactor);
        path.push('Z');
      }
      // tslint:disable-next-line:max-line-length
      this.lines.push({
        path:  path.join(' '),
        strokePath,
        // Legend label is the SERIES name (dataset.label), not the x-axis category (labels[i]).
        label: (this.config.data.datasets[i] && this.config.data.datasets[i].label)
                 ? this.config.data.datasets[i].label
                 : ('Series ' + (i + 1)),
        color: this.config.colors[i % this.config.colors.length],
        fill:  this.config.type === 'area' ? this.config.colors[i % this.config.colors.length] : 'none'
      });
      this.points.push(linePoints);
    }

    // Per-point tooltip text: every series' value at that x position (rendered by the
    // ngbPopover on the hover zones; '\n' relies on `white-space: pre-line` in the host css).
    this.pointTooltips = (this.config.data.labels || []).map((_, j) =>
      this.config.data.datasets
          .map((d) => (d.label ? d.label + ': ' : '') + (d.data[j] != null ? d.data[j] : ''))
          .join('\n'));
  }

  private getTranslatedY(value: number) {
    return this.config.chartHeight - this.config.yPadding - this.yOffsets - this.getFactoredValue(value);
  }

  getFactoredValue(value: number) {
    return value * this.yFactor;
  }

  private getLabelsAndValues(i) {
    const values = this.config.data.datasets.map((a) => (a.label ? (a.label + ':') : '') + a.data[i]);
    return [this.config.data.labels[i] || ''].concat(values);
  }

  private getTooltip(hoveredIndex) {
    const text     = this.getLabelsAndValues(hoveredIndex);
    const align    = hoveredIndex > this.config.data.labels.length / 2 ? 'left' : 'right';
    const tooltipX = this.config.xPadding + this.xOffsets + hoveredIndex * this.xStep + (align === 'left'
      ? 0
      : this.xStep / 2);
    const min      = Math.min.apply([], this.config.data.datasets.map((a) => a.data[hoveredIndex]));
    const tooltipY = this.getTranslatedY(min);
    // this.context.font = this.getFont(0.8, true);
    const colors   = this.config.isStacked || this.config.data.datasets.length > 1
      ? ['white', ...this.config.colors]
      : ['white', this.config.colors[hoveredIndex % this.config.colors.length]];
    return {text: text, x: tooltipX, y: tooltipY, align: align};
  }
}
